import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faRobot, 
  faUser, 
  faSpinner,
  faExclamationTriangle,
  faPlug,
  faPlugCircleCheck
} from '@fortawesome/free-solid-svg-icons';
import './AIChat.css';

const AIChat = () => {
  const [questionId, setQuestionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [websocket, setWebsocket] = useState(null);
  const [error, setError] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectToChat = async () => {
    if (!questionId.trim()) {
      setError('Please enter a question ID');
      return;
    }

    if (websocket) {
      // console.log('Closing existing WebSocket connection');
      websocket.close();
    }

    setIsConnecting(true);
    setError('');
    setMessages([]);

    try {
      // Get the API base URL from environment or use default
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      // console.log('API URL:', apiUrl);
      
      // Auth carried by the HttpOnly session cookie — the browser sends it
      // on both the HTTP test below and the WebSocket upgrade.
      const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      const wsEndpoint = `${wsUrl}/api/question/${questionId}/chat`;

      try {
        const testResponse = await fetch(`${apiUrl}/api/question/${questionId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        // console.log('📡 HTTP test response status:', testResponse.status);
        // console.log('📡 HTTP test response ok:', testResponse.ok);
        
        if (!testResponse.ok) {
          console.warn('⚠️ HTTP endpoint returned error:', testResponse.status, testResponse.statusText);
          if (testResponse.status === 404) {
            setError(`Question ${questionId} not found. Please check the question ID.`);
            setIsConnecting(false);
            return;
          } else if (testResponse.status === 401) {
            setError('Authentication failed. Please log in again.');
            setIsConnecting(false);
            return;
          }
        } else {
          // console.log('✅ HTTP endpoint is reachable');
        }
      } catch (httpError) {
        console.error('❌ HTTP test failed:', httpError);
        setError(`Backend server not reachable at ${apiUrl}. Is the server running?`);
        setIsConnecting(false);
        return;
      }

      // console.log('Creating WebSocket connection...');
      // console.log('🔐 Trying different authentication methods...');
      
      let ws;
      try {
        ws = new WebSocket(wsEndpoint);
      } catch (wsCreationError) {
        console.error('WebSocket creation failed:', wsCreationError);
        setError(`Failed to create WebSocket connection: ${wsCreationError.message}`);
        setIsConnecting(false);
        return;
      }
      
      // console.log('WebSocket object created:', ws);
      // console.log('WebSocket readyState:', ws.readyState); // 0 = CONNECTING

      ws.onopen = (event) => {
        clearTimeout(connectionTimeout);

        setTimeout(() => {
          try {
            ws.send('Hello, this is a test message');
          } catch (testError) {
            console.warn('Failed to send test message:', testError);
          }
        }, 100);

        setIsConnected(true);
        setIsConnecting(false);
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'system',
          content: `Connected to chat for Question ${questionId}`,
          timestamp: new Date()
        }]);
        setWebsocket(ws);
      };

      ws.onmessage = (event) => {
        // console.log('📨 Received WebSocket message:', event.data);
        // console.log('Message event details:', event);
        
        // Hide typing indicator when we receive a message
        setIsAiTyping(false);
        
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'bot',
          content: event.data,
          timestamp: new Date()
        }]);
      };

      ws.onclose = (event) => {
        // console.log('❌ WebSocket connection closed');
        // console.log('Close event code:', event.code);
        // console.log('Close event reason:', event.reason);
        // console.log('Close event wasClean:', event.wasClean);
        // console.log('Full close event:', event);
        
        // Clear the connection timeout
        clearTimeout(connectionTimeout);
        
        // Log common close codes
        const closeCodes = {
          1000: 'Normal Closure',
          1001: 'Going Away',
          1002: 'Protocol Error',
          1003: 'Unsupported Data',
          1005: 'No Status Received',
          1006: 'Abnormal Closure',
          1007: 'Invalid frame payload data',
          1008: 'Policy Violation',
          1009: 'Message too big',
          1010: 'Missing Extension',
          1011: 'Internal Error',
          1015: 'TLS Handshake'
        };
        
        // console.log('Close code meaning:', closeCodes[event.code] || 'Unknown');
        
        setIsConnected(false);
        setWebsocket(null);
        setIsConnecting(false);
        
        if (event.code !== 1000) { // 1000 is normal closure
          let errorMsg = `Connection closed (${event.code}): ${closeCodes[event.code] || 'Unknown error'}`;
          
          // Add specific guidance based on error code
          if (event.code === 1006) {
            errorMsg += ' - This usually means the WebSocket server is not running or the endpoint does not exist.';
          }
          
          if (event.reason) {
            errorMsg += ` - ${event.reason}`;
          } else {
            errorMsg += ' - No reason provided';
          }
          
          // console.log('Adding error message to chat:', errorMsg);
          setMessages(prev => [...prev, {
            id: Date.now(),
            type: 'system',
            content: errorMsg,
            timestamp: new Date()
          }]);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error occurred');
        console.error('Error event:', error);
        console.error('WebSocket readyState during error:', ws.readyState);
        console.error('WebSocket URL during error:', ws.url);
        
        // Try to get more error details
        if (error.target) {
          console.error('Error target:', error.target);
          console.error('Error target readyState:', error.target.readyState);
        }
        
        // Check if this is a network connectivity issue
        if (navigator.onLine === false) {
          console.error('🌐 Network is offline');
          setError('No internet connection detected. Please check your network.');
        } else {
          // console.log('🌐 Network appears to be online');
          
          // More specific error message based on what we know
          let errorMsg = 'Failed to connect to chat service. ';
          if (ws.url.includes('token=')) {
            errorMsg += 'The backend might not support token authentication via query parameter. ';
          }
          errorMsg += 'Please check that the WebSocket endpoint is properly configured on the backend.';
          
          setError(errorMsg);
        }
        
        setIsConnecting(false);
        setIsConnected(false);
      };

      // Add connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          console.error('⏰ WebSocket connection timeout after 5 seconds');
          ws.close();
          setError('Connection timeout. The WebSocket server may not be responding.');
          setIsConnecting(false);
        }
      }, 5000);

      // Log the WebSocket state after a short delay
      setTimeout(() => {
        // console.log('WebSocket state after 100ms:', {
        //   readyState: ws.readyState,
        //   url: ws.url,
        //   protocol: ws.protocol,
        //   extensions: ws.extensions
        // });
      }, 100);

    } catch (err) {
      console.error('❌ Exception during WebSocket connection setup:');
      console.error('Exception details:', err);
      console.error('Exception stack:', err.stack);
      setError('Failed to establish connection: ' + err.message);
      setIsConnecting(false);
    }
  };

  const disconnectFromChat = () => {
    if (websocket) {
      websocket.close(1000, 'User disconnected');
    }
    setIsConnected(false);
    setWebsocket(null);
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'system',
      content: 'Disconnected from chat',
      timestamp: new Date()
    }]);
  };

  const sendMessage = () => {
    // console.log('📤 Attempting to send message...');
    // console.log('Input message:', inputMessage);
    // console.log('WebSocket exists:', !!websocket);
    // console.log('Is connected:', isConnected);
    
    if (!inputMessage.trim() || !websocket || !isConnected) {
      // console.log('❌ Cannot send message - missing requirements');
      return;
    }

    const message = inputMessage.trim();
    // console.log('📤 Sending message:', message);
    // console.log('WebSocket readyState before send:', websocket.readyState);
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    }]);

    try {
      // Send message through WebSocket
      websocket.send(message);
      // console.log('✅ Message sent successfully');
      setInputMessage('');
      
      // Show typing indicator after sending a message
      setIsAiTyping(true);
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'system',
        content: `Failed to send message: ${err.message}`,
        timestamp: new Date()
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="ai-chat-container">
      <div className="ai-chat-header">
        <h1>
          <FontAwesomeIcon icon={faRobot} className="header-icon" />
          AI Chat
        </h1>
        <p>Test the chat service by connecting to a question and starting a conversation</p>
        <div className="status-summary">
          <div className="status-item success">
            <span>✅ Frontend Implementation</span>
            <small>AI Chat page created with WebSocket support</small>
          </div>
          <div className="status-item success">
            <span>✅ Authentication</span>
            <small>JWT token integration working</small>
          </div>
          <div className="status-item success">
            <span>✅ Backend HTTP API</span>
            <small>Questions endpoint reachable</small>
          </div>
          <div className="status-item success">
            <span>✅ WebSocket Chat Endpoint</span>
            <small>Backend endpoint working - tested with Postman</small>
          </div>
        </div>
      </div>

      <div className="chat-connection-panel">
        <div className="connection-controls">
          <div className="question-id-input">
            <label htmlFor="questionId">Question ID:</label>
            <input
              id="questionId"
              type="number"
              value={questionId}
              onChange={(e) => setQuestionId(e.target.value)}
              placeholder="Enter question ID (e.g., 123)"
              disabled={isConnected}
            />
          </div>
          
          <div className="connection-buttons">
            {!isConnected ? (
              <button 
                onClick={connectToChat}
                disabled={isConnecting}
                className="connect-btn"
              >
                {isConnecting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Connecting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlug} />
                    Connect to Chat
                  </>
                )}
              </button>
            ) : (
              <button 
                onClick={disconnectFromChat}
                className="disconnect-btn"
              >
                <FontAwesomeIcon icon={faPlugCircleCheck} />
                Disconnect
              </button>
            )}
          </div>
        </div>

        <div className="connection-status">
          <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            <div className="status-dot"></div>
            <span>{isConnected ? 'Connected' : 'Disconnected'}</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            {error}
          </div>
        )}
      </div>

      <div className="chat-messages-container">
        <div className="chat-messages">
          {messages.length === 0 ? (
            <div className="empty-chat">
              <FontAwesomeIcon icon={faRobot} className="empty-icon" />
              <p>Connect to a question to start chatting</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-avatar">
                  <FontAwesomeIcon 
                    icon={
                      message.type === 'user' ? faUser : 
                      message.type === 'bot' ? faRobot : 
                      faExclamationTriangle
                    } 
                  />
                </div>
                <div className="message-content">
                  <div className="message-text">{message.content}</div>
                  <div className="message-time">{formatTime(message.timestamp)}</div>
                </div>
              </div>
            ))
          )}
          
          {/* AI Typing Indicator */}
          {isAiTyping && (
            <div className="message bot typing-indicator">
              <div className="message-avatar">
                <FontAwesomeIcon icon={faRobot} />
              </div>
              <div className="message-content">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isConnected ? "Type your message..." : "Connect to a question first"}
            disabled={!isConnected}
            rows="2"
          />
          <button 
            onClick={sendMessage}
            disabled={!isConnected || !inputMessage.trim()}
            className="send-btn"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
